import React from 'react';
import { contactData, logoUrl } from '../data/mock';
import { Phone, Mail, MapPin, Instagram, Facebook, Heart, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About Us', href: '#about' },
    { name: 'Amenities', href: '#amenities' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#2C2C2C] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={logoUrl}
              alt="Elite Marriage Lawns"
              className="h-20 w-auto mb-6"
            />
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Allaganj's premier wedding destination offering elegant spaces and world-class amenities for your most cherished celebrations.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href={contactData.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#C9A962] hover:border-[#C9A962] transition-all group"
                aria-label="Instagram"
              >
                <Instagram size={18} className="text-white/70 group-hover:text-white transition-colors" />
              </a>
              <a
                href={contactData.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#C9A962] hover:border-[#C9A962] transition-all group"
                aria-label="Facebook"
              >
                <Facebook size={18} className="text-white/70 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg text-[#C9A962] mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-white/70 hover:text-[#C9A962] transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-serif text-lg text-[#C9A962] mb-6">Our Services</h4>
            <ul className="space-y-3">
              <li className="text-white/70 text-sm">Wedding Ceremonies</li>
              <li className="text-white/70 text-sm">Reception Parties</li>
              <li className="text-white/70 text-sm">Engagement Functions</li>
              <li className="text-white/70 text-sm">Corporate Events</li>
              <li className="text-white/70 text-sm">Birthday Celebrations</li>
              <li className="text-white/70 text-sm">Anniversary Parties</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg text-[#C9A962] mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${contactData.phone}`}
                  className="flex items-start gap-3 text-white/70 hover:text-[#C9A962] transition-colors group"
                >
                  <Phone size={18} className="text-[#C9A962] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{contactData.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${contactData.email}`}
                  className="flex items-start gap-3 text-white/70 hover:text-[#C9A962] transition-colors group"
                >
                  <Mail size={18} className="text-[#C9A962] flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{contactData.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={contactData.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-white/70 hover:text-[#C9A962] transition-colors group"
                >
                  <MapPin size={18} className="text-[#C9A962] flex-shrink-0 mt-0.5" />
                  <span className="text-sm leading-relaxed">{contactData.address}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm text-center md:text-left">
            © {currentYear} Elite Marriage Lawns. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <span>Made with</span>
            <Heart size={14} className="text-[#C9A962] fill-[#C9A962]" />
            <span>in Allaganj, UP</span>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-[#C9A962] text-white shadow-lg hover:bg-[#B8944D] transition-colors flex items-center justify-center z-40"
        aria-label="Scroll to top"
      >
        <ArrowUp size={20} />
      </button>
    </footer>
  );
};

export default Footer;
